import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface CoiProfile {
  id: string;
  contactId: string;
  specialty: string | null;
  firmName: string | null;
  specialtyNotes: string | null;
  howWeMet: string | null;
}

interface COIContact {
  id: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  employerName?: string | null;
  firmId?: string | null;
  // Flat since 2026-09-02: the COI detail payload is derived and carries these at the top level.
  specialty?: string | null; firmName?: string | null; howWeMet?: string | null;
  specialtyNotes?: string | null; geographicCoverage?: string | null;
}

interface EditCOIDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  coi: COIContact | null;
}

export function EditCOIDialog({ open, onOpenChange, coi }: EditCOIDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    email: "",
    phone: "",
    specialty: "other",
    employerName: "",
    firmName: "",
    howWeMet: "",
    specialtyNotes: "",
    geographicCoverage: "",
    notes: "",
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (coi) {
      setFormData({
        name: coi.name || "",
        title: coi.title || "",
        email: coi.email || "",
        phone: coi.phone || "",
        specialty: coi.specialty || "other",
        employerName: coi.employerName || "",
        firmName: coi.firmName || "",
        howWeMet: coi.howWeMet || "",
        specialtyNotes: coi.specialtyNotes || "",
        geographicCoverage: coi.geographicCoverage || "",
        notes: coi.notes || "",
      });
    }
  }, [coi]);

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch(`/api/cois/${coi?.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to update COI");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cois"] });
      queryClient.invalidateQueries({ queryKey: ["coi", coi?.id] });
      toast.success("COI updated successfully");
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    updateMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Center of Influence</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-specialty">Specialty</Label>
              <Select value={formData.specialty} onValueChange={(v) => setFormData(f => ({ ...f, specialty: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attorney">Attorney</SelectItem>
                  <SelectItem value="cpa">CPA</SelectItem>
                  <SelectItem value="banker">Banker</SelectItem>
                  <SelectItem value="wealth_advisor">Wealth Advisor</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!coi?.firmId && (
              <div className="space-y-2">
                <Label htmlFor="edit-employerName">Firm / Employer</Label>
                <Input
                  id="edit-employerName"
                  value={formData.employerName}
                  onChange={(e) => setFormData(f => ({ ...f, employerName: e.target.value }))}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-howWeMet">How did you meet?</Label>
              <Input
                id="edit-howWeMet"
                value={formData.howWeMet}
                onChange={(e) => setFormData(f => ({ ...f, howWeMet: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-geoCoverage">Geographic Coverage</Label>
              <Input
                id="edit-geoCoverage"
                value={formData.geographicCoverage}
                onChange={(e) => setFormData(f => ({ ...f, geographicCoverage: e.target.value }))}
                placeholder="e.g. Dallas/Fort Worth M&A, Oklahoma energy"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-specialtyNotes">Specialty Notes</Label>
              <Input
                id="edit-specialtyNotes"
                value={formData.specialtyNotes}
                onChange={(e) => setFormData(f => ({ ...f, specialtyNotes: e.target.value }))}
                placeholder="Areas of expertise, certifications, etc."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
